import { useState } from 'react'
import {
  IconAlertCircle,
  IconCircleCheck,
  IconCircleDashed,
  IconCircleX,
  type Icon,
} from '@tabler/icons-react'
import type { IdentityVerificationData } from '@/hooks/useIdentityVerification'
import { formatDateTime } from '@/utils'

type PersonaAttempt = NonNullable<IdentityVerificationData['latestAttempt']>
type DecisionReason = NonNullable<PersonaAttempt['decisionReason']>
type MatchOutcome = NonNullable<PersonaAttempt['nameMatch']>

const REASON_LABELS: Record<DecisionReason, string> = {
  persona_negative: 'Persona failed, declined or expired the verification',
  id_not_passed: 'The government ID did not pass',
  id_expired: 'The government ID has expired',
  id_expiry_unknown: "The ID's expiration date could not be read",
  selfie_not_passed: 'The selfie (face match and liveness) did not pass',
  demographics_unavailable: 'The ID or the chart has no name or date of birth to compare',
  name_mismatch: "The name on the ID doesn't match the chart",
  dob_mismatch: "The date of birth on the ID doesn't match the chart",
}

const MATCH_LABELS: Record<MatchOutcome, string> = {
  match: 'Matches',
  mismatch: "Doesn't match",
  unavailable: 'Not available',
}

type Outcome = 'passed' | 'failed' | 'inconclusive' | 'not_done'

const OUTCOMES: Record<Outcome, { label: string; icon: Icon; className: string }> = {
  passed: { label: 'Passed', icon: IconCircleCheck, className: 'text-green-700' },
  failed: { label: 'Failed', icon: IconCircleX, className: 'text-red-700' },
  inconclusive: { label: 'Inconclusive', icon: IconAlertCircle, className: 'text-amber-700' },
  not_done: { label: 'Not done', icon: IconCircleDashed, className: 'text-slate-500' },
}

/**
 * One of Persona's check codes as the card's result. Anything Persona has not settled
 * (`requires_retry`, `tentatively_*`, `submitted`) is inconclusive; a check never reached is not done.
 */
function checkOutcome(code: string | undefined): Outcome {
  if (code === 'passed' || code === 'confirmed') return 'passed'
  if (code === 'failed') return 'failed'
  if (!code || code === 'initiated' || code === 'skipped' || code === 'canceled') return 'not_done'
  return 'inconclusive'
}

/** Name and date of birth as one result; the two stay separate under the details. */
function demographicsOutcome(attempt: PersonaAttempt): Outcome {
  const { nameMatch, dobMatch } = attempt
  if (!nameMatch || !dobMatch) return 'not_done'
  if (nameMatch === 'match' && dobMatch === 'match') return 'passed'
  if (nameMatch === 'mismatch' || dobMatch === 'mismatch') return 'failed'
  return 'inconclusive'
}

/** Persona's own check codes (`passed`, `requires_retry`, …) as words. */
const checkLabel = (code: string | undefined) =>
  code ? code.charAt(0).toUpperCase() + code.slice(1).replace(/_/g, ' ') : 'Not done'

const dateTime = (value: string | undefined) => (value ? formatDateTime(value, '/', true) : '—')

function Result({ label, outcome }: { label: string; outcome: Outcome }) {
  const { label: text, icon: OutcomeIcon, className } = OUTCOMES[outcome]
  return (
    <div className="px-1" data-testid="persona-result">
      <p className="text-xs font-medium text-slate-900">{label}</p>
      <p className={`mt-1 flex items-center gap-1 text-sm ${className}`}>
        <OutcomeIcon size={16} aria-hidden />
        {text}
      </p>
    </div>
  )
}

/**
 * The patient's latest Persona attempt on the Identity Verification card (docs/PERSONA_IDV_PLAN.md
 * §9 "Target card design"): why it went to review, the three check results, and the stored detail
 * behind **View Persona details**. The middleware sends it only to care coordinators and doctors
 * (§8), so nothing here gates it.
 */
export function PersonaAttemptSummary({ attempt }: { attempt: PersonaAttempt }) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const review = attempt.decision === 'review'

  return (
    <div className="text-sm text-slate-700" data-testid="persona-attempt">
      {attempt.decision ? (
        <div
          className={`grid gap-3 rounded-lg border border-[#E2E8F0] p-3 ${review ? 'sm:grid-cols-4' : 'sm:grid-cols-3'}`}
        >
          {review ? (
            <div className="rounded-md bg-amber-50 p-2">
              <p className="text-xs font-medium text-slate-900">Reason</p>
              <p className="mt-1 text-sm">
                {attempt.decisionReason ? REASON_LABELS[attempt.decisionReason] : 'See details'}
              </p>
            </div>
          ) : null}
          <Result label="Government ID" outcome={checkOutcome(attempt.governmentIdResult)} />
          <Result label="Selfie & liveness" outcome={checkOutcome(attempt.selfieResult)} />
          <Result label="Demographics match" outcome={demographicsOutcome(attempt)} />
        </div>
      ) : attempt.closedReason === 'patient_verified' ? (
        <p>
          Closed: the patient was verified another way before finishing · started{' '}
          {dateTime(attempt.startedAt)}
        </p>
      ) : attempt.abandonedAt ? (
        <p>
          Not finished · started {dateTime(attempt.startedAt)} · sent to review{' '}
          {dateTime(attempt.abandonedAt)}
        </p>
      ) : (
        <p>In progress at Persona · started {dateTime(attempt.startedAt)}</p>
      )}
      <button
        type="button"
        className="text-primary mt-2 text-sm font-medium hover:underline"
        aria-expanded={detailsOpen}
        onClick={() => setDetailsOpen((open) => !open)}
      >
        {detailsOpen ? 'Hide Persona details' : 'View Persona details'}
      </button>
      {detailsOpen ? (
        <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-xs">
          <dt className="text-[#64748B]">Started</dt>
          <dd>{dateTime(attempt.startedAt)}</dd>
          <dt className="text-[#64748B]">Finished</dt>
          <dd>{dateTime(attempt.completedAt)}</dd>
          <dt className="text-[#64748B]">Persona status</dt>
          <dd>{checkLabel(attempt.inquiryStatus)}</dd>
          <dt className="text-[#64748B]">Government ID</dt>
          <dd>{checkLabel(attempt.governmentIdResult)}</dd>
          <dt className="text-[#64748B]">Selfie & liveness</dt>
          <dd>{checkLabel(attempt.selfieResult)}</dd>
          <dt className="text-[#64748B]">Name</dt>
          <dd>{attempt.nameMatch ? MATCH_LABELS[attempt.nameMatch] : '—'}</dd>
          <dt className="text-[#64748B]">Date of birth</dt>
          <dd>{attempt.dobMatch ? MATCH_LABELS[attempt.dobMatch] : '—'}</dd>
          <dt className="text-[#64748B]">Document</dt>
          <dd>
            {[attempt.documentType?.toUpperCase(), attempt.issuingJurisdiction]
              .filter(Boolean)
              .join(' · ') || '—'}
          </dd>
          <dt className="text-[#64748B]">Decision rule</dt>
          <dd>{attempt.policyVersion ?? '—'}</dd>
          <dt className="text-[#64748B]">Persona inquiry</dt>
          <dd className="font-mono">{attempt.inquiryId ?? '—'}</dd>
        </dl>
      ) : null}
    </div>
  )
}
